import type { HTMLAttributes } from 'react';

import { cn } from '../utils/cn';

import { UKLADY_LOGO } from './geometria';

/**
 * Hasło marki pod znakiem — „CZUJE · ŁĄCZY · DZIAŁA" (decyzja właściciela,
 * 05.10.2026; para EN przez i18n: „SENSE · CONNECT · ACT"). Trzy słowa
 * opowiadają znak: odłamki-organy CZUJĄ, rdzeń-Nexus ŁĄCZY, całość DZIAŁA.
 *
 * Szerokość hasła równa się szerokości znaku, przy którym stoi, CO DO
 * PIKSELA: szerokość „prawie równa" napisowi wygląda na pomyłkę (zmierzone
 * przy wyborze hasła: 91,6 % szerokości napisu czytało się jak błąd składu).
 * Dlatego hasło jest tekstem SVG z `textLength` i `lengthAdjust="spacing"` —
 * przeglądarka sama dobiera rozstrzelenie do zadanej szerokości, niezależnie
 * od wersji kroju, tego, czy font już doszedł, i od języka tłumaczenia.
 * Ręcznie dobrane `letter-spacing` rozjechałoby się przy pierwszej zmianie
 * Intera albo przy przełączeniu na angielski.
 *
 * Wersaliki stoją od górnej krawędzi pudełka (linia bazowa na wysokości
 * wersalików Intera), więc odstęp nad hasłem jest odstępem do liter, a nie
 * do pustego miejsca interlinii. Ogonki (Ą, Ę) wystają w dół poza pudełko
 * (`overflow: visible`).
 *
 * Dla czytnika ekranu hasło jest zwykłym zdaniem („Czuje, łączy, działa"),
 * nie literowanymi wersalikami z „kropką środkową".
 *
 * Komponent serwerowy (bez hooków).
 */
export interface HasloInsolitumProps extends HTMLAttributes<HTMLSpanElement> {
  /** Treść hasła w bieżącym języku (z i18n), wersalikami, człony rozdzielone „·". */
  tekst: string;
  /** Szerokość znaku, pod którym stoi hasło — `szerokoscZnaku(uklad, wysokosc)`. */
  szerokosc: number;
}

/** Stopień hasła: 11 px, waga 600 (Inter) — nadtytuł, nie nagłówek. */
export const STOPIEN_HASLA_PX = 11;
/** Wysokość wersalików Intera (0,727 em) — linia bazowa hasła. */
const LINIA_BAZOWA = +(STOPIEN_HASLA_PX * 0.727).toFixed(2);

/**
 * Szerokość znaku w danym układzie przy danej wysokości. W układzie
 * pionowym napis zajmuje całą szerokość, więc to szerokość napisu
 * INSOLITUM; w poziomym — całego znaku (sygnet + napis), bo hasło należy do
 * Insolitum, a nie do nazwy produktu stojącej za kreską.
 */
export function szerokoscZnaku(uklad: 'pionowe' | 'poziome', wysokosc: number): number {
  const u = UKLADY_LOGO[uklad];
  return (wysokosc * u.szerokosc) / u.wysokosc;
}

/** „CZUJE · ŁĄCZY · DZIAŁA" → „Czuje, łączy, działa" (dla czytnika ekranu). */
export function hasloDlaCzytnika(tekst: string): string {
  const zdanie = tekst
    .split('·')
    .map((s) => s.trim().toLocaleLowerCase())
    .filter(Boolean)
    .join(', ');
  return zdanie.charAt(0).toLocaleUpperCase() + zdanie.slice(1);
}

export function HasloInsolitum({ tekst, szerokosc, className, ...props }: HasloInsolitumProps) {
  const w = +szerokosc.toFixed(2);
  return (
    <span className={cn('ids-haslo', className)} {...props}>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox={`0 0 ${w} ${STOPIEN_HASLA_PX}`}
        width={w}
        height={STOPIEN_HASLA_PX}
        overflow="visible"
        aria-hidden
        focusable="false"
      >
        <text x="0" y={LINIA_BAZOWA} textLength={w} lengthAdjust="spacing">
          {tekst}
        </text>
      </svg>
      <span className="ids-haslo__czytnik">{hasloDlaCzytnika(tekst)}</span>
    </span>
  );
}
